import { type FormEvent, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { Eye, EyeOff } from "lucide-react";
import {
  cancelTwoFactor,
  signIn,
  signInWithCode,
  type SignInCodeOutcome,
  type SignInOutcome,
} from "@/shared/api/session";
import { useTranslation } from "@/shared/i18n/LocaleProvider";
import { Button } from "@/shared/ui/primitives/Button";
import { useSession } from "@/features/auth/hooks/use-session";
import {
  AuthCard,
  AuthLayout,
  ErrorBanner,
  Field,
  PrimaryButton,
  Wordmark,
} from "@/features/auth/components/AuthPrimitives";
import {
  representationPreviewEnabled,
  resetRepresentationPreview,
} from "@/features/auth/lib/representation-preview";

export function SignInPage({ notice }: { notice?: string }) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { status } = useSession();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState<"credentials" | "code">("credentials");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const reason = params.get("reason");
  const accessMessage =
    reason === "advisor_required"
      ? t("auth.errors.advisorRequired")
      : reason === "advisor_unavailable"
        ? t("auth.errors.advisorUnavailable")
        : "";

  useEffect(() => {
    if (representationPreviewEnabled) resetRepresentationPreview();
    if (status === "authenticated") window.location.replace("/");
  }, [status]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!password) {
      setError(t("auth.validation.passwordRequired"));
      return;
    }
    setLoading(true);
    setError("");
    const outcome = await signIn(identifier.trim(), password);
    if (outcome.ok) return;
    setLoading(false);
    if (outcome.reason === "two_factor_required" && outcome.status === "TFAL") {
      setStep("code");
      return;
    }
    setError(
      t(
        outcome.reason === "two_factor_required"
          ? "auth.errors.twoFactorUnconfirmed"
          : ERROR_KEY[outcome.reason],
      ),
    );
  }

  async function handleCode(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    const outcome = await signInWithCode(code.trim());
    if (outcome.ok) return;
    setLoading(false);
    if (outcome.reason === "expired") {
      backToCredentials();
      setError(t("auth.errors.codeExpired"));
      return;
    }
    setError(t(CODE_ERROR_KEY[outcome.reason]));
  }

  function backToCredentials() {
    cancelTwoFactor();
    setStep("credentials");
    setCode("");
    setError("");
  }

  const message = error || accessMessage;
  if (step === "code") {
    return (
      <AuthLayout>
        <Wordmark />
        <AuthCard title={t("auth.signin.title")} subtitle={t("auth.twoFactor.lead")}>
          <form onSubmit={handleCode} className="flex flex-col gap-[16px]">
            {message && <ErrorBanner>{message}</ErrorBanner>}
            <Field
              id="email"
              type="text"
              label={t("auth.fields.identifier")}
              value={identifier}
              readOnly
            />
            <Field
              id="code"
              type="text"
              label={t("auth.fields.code")}
              value={code}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
              required
              onChange={(event) => setCode(event.target.value)}
            />
            <PrimaryButton type="submit" disabled={loading || code.trim().length === 0}>
              {loading ? t("auth.signin.submitLoading") : t("auth.signin.submitLabel")}
            </PrimaryButton>
            <Button type="button" variant="ghost" onClick={backToCredentials} className="w-full">
              {t("auth.twoFactor.back")}
            </Button>
          </form>
        </AuthCard>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout>
      <Wordmark />
      <AuthCard title={t("auth.signin.title")}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-[16px]">
          {notice && (
            <p className="m-0 text-center text-md leading-[1.5] text-[var(--color-text-muted)]">
              {notice}
            </p>
          )}
          {message && <ErrorBanner>{message}</ErrorBanner>}
          <Field
            id="email"
            type="text"
            label={t("auth.fields.identifier")}
            value={identifier}
            autoComplete="username"
            autoFocus
            required
            onChange={(event) => setIdentifier(event.target.value)}
          />
          <Field
            id="password"
            type={showPassword ? "text" : "password"}
            label={t("auth.fields.password")}
            value={password}
            autoComplete="current-password"
            onChange={(event) => setPassword(event.target.value)}
            trailing={
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                aria-pressed={showPassword}
                aria-label={
                  showPassword ? t("auth.fields.hidePassword") : t("auth.fields.showPassword")
                }
                className="inline-flex size-[32px] cursor-pointer items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-faint)] transition-colors duration-[var(--duration-fast)] hover:text-[var(--color-text)] focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] focus-visible:outline-none"
              >
                {showPassword ? <EyeOff size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
              </button>
            }
          />
          <PrimaryButton type="submit" disabled={loading || !identifier || !password}>
            {loading ? t("auth.signin.submitLoading") : t("auth.signin.submitLabel")}
          </PrimaryButton>
        </form>
      </AuthCard>
    </AuthLayout>
  );
}

const ERROR_KEY: Record<
  Exclude<Extract<SignInOutcome, { ok: false }>["reason"], "two_factor_required">,
  string
> = {
  invalid_credentials: "auth.errors.invalidCredentials",
  unreachable: "auth.errors.unreachable",
};

const CODE_ERROR_KEY: Record<
  Exclude<Extract<SignInCodeOutcome, { ok: false }>["reason"], "expired">,
  string
> = {
  invalid_code: "auth.errors.invalidCode",
  unreachable: "auth.errors.unreachable",
};
